import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check, Loader2, Lock, Shield, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useGroupMembership } from '@/hooks/useGroupMembership';
import { fetchSeriesOdds, placeBet, isCommissioner, fetchSeriesPicksForGroup } from '@/lib/supabase-queries';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import Layout from '@/components/Layout';
import { TeamLogo } from '@/components/TeamLogo';
import { PicksRevealSheet, type RevealPick } from '@/components/PicksRevealSheet';
import { trackEvent } from '@/lib/analytics';
import { exactScorePoints } from '@/lib/scoring';

const WINNER_POINTS = 2;

function getDifficultyOpacity(multiplier: number) {
  // Higher multiplier = harder = more intense bg
  if (multiplier >= 10) return 'bg-accent/20';
  if (multiplier >= 5) return 'bg-accent/15';
  if (multiplier >= 3) return 'bg-accent/10';
  return 'bg-accent/5';
}

const BetPlacement = () => {
  const { groupId, seriesId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { isMember, loading: membershipLoading } = useGroupMembership(groupId, user?.id);
  const { toast } = useToast();

  const godmodeUserId = searchParams.get('godmode');

  const [s, setS] = useState<any>(null);
  const [odds, setOdds] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [selectedOutcome, setSelectedOutcome] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // God mode state
  const [isGodMode, setIsGodMode] = useState(false);
  const [targetDisplayName, setTargetDisplayName] = useState<string | null>(null);

  // Edit-mode state (user already has a bet on this series)
  const [hasExistingBet, setHasExistingBet] = useState(false);

  // Reveal sheet state
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealPicks, setRevealPicks] = useState<RevealPick[]>([]);
  const [revealLoading, setRevealLoading] = useState(false);
  const [totalMembers, setTotalMembers] = useState(0);

  useEffect(() => {
    if (!seriesId) return;
    const load = async () => {
      setLoading(true);
      const [{ data: series }, oddsData, membersRes] = await Promise.all([
        supabase.from('series').select('*').eq('id', seriesId).single(),
        fetchSeriesOdds(seriesId),
        groupId
          ? supabase.from('group_members').select('user_id').eq('group_id', groupId)
          : Promise.resolve({ data: [] as any[] }),
      ]);
      setS(series);
      setOdds(oddsData);
      setTotalMembers(membersRes.data?.length ?? 0);

      // Check god mode
      let godMode = false;
      if (godmodeUserId && user && groupId) {
        const isComm = await isCommissioner(user.id, groupId);
        if (isComm && godmodeUserId !== user.id) {
          godMode = true;
          setIsGodMode(true);
          // Fetch target user display name
          const { data: targetMember } = await supabase
            .from('group_members')
            .select('display_name')
            .eq('user_id', godmodeUserId)
            .eq('group_id', groupId)
            .maybeSingle();
          setTargetDisplayName(targetMember?.display_name ?? godmodeUserId);
        }
      }

      // Pre-populate existing bet so users see their current pick and can edit
      const targetUserId = godMode && godmodeUserId ? godmodeUserId : user?.id;
      if (targetUserId && groupId) {
        const { data: existingBet } = await supabase
          .from('bets')
          .select('predicted_winner, predicted_score')
          .eq('series_id', seriesId)
          .eq('group_id', groupId)
          .eq('user_id', targetUserId)
          .maybeSingle();

        if (existingBet) {
          setSelectedTeam(existingBet.predicted_winner);
          setSelectedOutcome(existingBet.predicted_score);
          setHasExistingBet(true);
        }
      }

      setLoading(false);
    };
    load();
  }, [seriesId, godmodeUserId, user, groupId]);

  if (!membershipLoading && isMember === false) return <Navigate to="/" replace />;

  if (loading || membershipLoading) {
    return <Layout><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-accent" /></div></Layout>;
  }

  if (!s) return <Layout><div className="container mx-auto px-4 py-20 text-center">סדרה לא נמצאה</div></Layout>;

  const filteredOdds = selectedTeam ? odds.filter(o => o.team === selectedTeam) : [];
  const chosenOdd = odds.find(o => o.outcome === selectedOutcome);
  const isPastDeadline = s.betting_deadline && new Date(s.betting_deadline) < new Date();
  const canReveal = s.status === 'in_progress' || s.status === 'completed';

  const openReveal = async () => {
    if (!groupId || !seriesId) return;
    setRevealOpen(true);
    setRevealLoading(true);
    setRevealPicks([]);
    try {
      const picks = await fetchSeriesPicksForGroup(groupId, seriesId);
      const mapped: RevealPick[] = picks.map((p) => {
        const match = p.predicted_score.match(/(\d+)-(\d+)/);
        const label = match
          ? `${p.predicted_winner} in ${Number(match[1]) + Number(match[2])}`
          : p.predicted_winner;
        return {
          user_id: p.user_id,
          display_name: p.display_name,
          avatar_id: p.avatar_id,
          label,
        };
      });
      setRevealPicks(mapped);
    } catch (err) {
      console.warn('fetchSeriesPicksForGroup failed:', err);
    } finally {
      setRevealLoading(false);
    }
  };

  if (isPastDeadline && !isGodMode) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-20 text-center font-leon">
          <Lock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">ההימורים נסגרו</h2>
          <p className="text-muted-foreground mb-6">
            {canReveal
              ? 'הסדרה התחילה — אפשר לראות מה חברי הקבוצה ניחשו'
              : 'הבחירות יחשפו כשהסדרה תתחיל'}
          </p>
          {canReveal && (
            <Button
              onClick={openReveal}
              className="bg-accent hover:bg-accent/90 text-accent-foreground mb-4"
            >
              <Users className="w-4 h-4 ml-2" />
              ראה בחירות קבוצה
            </Button>
          )}
          <div>
            <Link to={`/group/${groupId}`} className="text-sm text-accent hover:underline">
              חזרה לקבוצה
            </Link>
          </div>
        </div>
        <PicksRevealSheet
          open={revealOpen}
          onClose={() => setRevealOpen(false)}
          title={`${s.team_home} vs ${s.team_away}`}
          subtitle={revealLoading ? 'טוען...' : 'בחירות חברי הקבוצה'}
          picks={revealPicks}
          currentUserId={user?.id}
          totalMembers={totalMembers}
        />
      </Layout>
    );
  }

  const effectiveUserId = isGodMode && godmodeUserId ? godmodeUserId : user?.id;

  const handleSubmit = async () => {
    if (!effectiveUserId || !groupId || !seriesId || !selectedOutcome || !chosenOdd) return;
    if (isPastDeadline && !isGodMode) {
      toast({ title: 'מועד ההימור עבר', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await placeBet({
        seriesId,
        groupId,
        userId: effectiveUserId,
        predictedWinner: chosenOdd.team,
        predictedScore: selectedOutcome,
        multiplier: chosenOdd.multiplier,
        oddsAtBet: chosenOdd.american_odds ?? null,
        isGodMode,
      });
      setSubmitted(true);
      const toastMsg = isGodMode
        ? `ההימור עבור ${targetDisplayName} נשמר!`
        : hasExistingBet
        ? 'ההימור עודכן!'
        : 'ההימור נשמר!';
      toast({ title: toastMsg, description: `בחרת ${selectedOutcome} · עד ${exactScorePoints(chosenOdd.american_odds ?? null)} נק'` });
      trackEvent('bet_placed', { groupId: groupId ?? undefined, metadata: { seriesId } });
    } catch (err: any) {
      toast({ title: 'שגיאה', description: err.message, variant: 'destructive' });
    }
    setSubmitting(false);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-10 max-w-2xl pb-32 font-leon">
        <Link to={`/group/${groupId}`} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-6">
          <ArrowRight className="w-4 h-4 rotate-180" /> חזרה לקבוצה
        </Link>

        {/* Edit-mode banner (regular user editing their own bet) */}
        {hasExistingBet && !isGodMode && !submitted && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-accent/10 border border-accent/30 rounded-2xl p-4 mb-6 flex items-center gap-3"
          >
            <Check className="w-5 h-5 text-accent shrink-0" />
            <div>
              <p className="text-sm font-semibold text-accent">עריכת הימור קיים</p>
              <p className="text-xs text-muted-foreground">ניתן לעדכן עד מועד סגירת ההימורים</p>
            </div>
          </motion.div>
        )}

        {/* God mode banner */}
        {isGodMode && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-accent/10 border-2 border-accent/40 rounded-2xl p-4 mb-6 flex items-center gap-3"
          >
            <Shield className="w-5 h-5 text-accent shrink-0" />
            <div>
              <p className="text-sm font-semibold text-accent">מצב ניהול</p>
              <p className="text-xs text-muted-foreground">הנחת הימור עבור {targetDisplayName}</p>
            </div>
          </motion.div>
        )}

        {/* Main card -- scouting report style */}
        <div className={`relative bg-card/80 backdrop-blur-sm border rounded-lg p-6 md:p-10 ${isGodMode ? 'border-accent/40' : 'border-accent/40'}`}>
          {/* Frame corners */}
          <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-accent rounded-tr-lg pointer-events-none" />
          <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-accent rounded-tl-lg pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-accent rounded-br-lg pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-accent rounded-bl-lg pointer-events-none" />

          <p className="section-label mb-1">ניחוש סדרה</p>
          <p className="font-mono text-xs tracking-widest text-accent mb-6 opacity-60">{seriesId?.substring(0, 8).toUpperCase()}</p>

          <AnimatePresence mode="wait">
            {!submitted ? (
              <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                {/* Step 1: Pick winner */}
                <p className="text-sm font-medium mb-4">שלב 1: מי תנצח?</p>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {[s.team_home, s.team_away].map((team: string) => {
                    const isSelected = selectedTeam === team;
                    return (
                      <button
                        key={team}
                        onClick={() => { setSelectedTeam(team); setSelectedOutcome(null); }}
                        disabled={isPastDeadline && !isGodMode}
                        className={`relative bg-secondary rounded-xl p-6 text-center transition-all ${
                          isSelected
                            ? 'border-2 border-accent btn-accent-glow'
                            : 'border border-border opacity-60 hover:opacity-100'
                        } disabled:opacity-30 disabled:cursor-not-allowed`}
                      >
                        <TeamLogo teamName={team} size={56} className="mx-auto mb-3" />
                        <p className="font-semibold text-sm">{team}</p>
                      </button>
                    );
                  })}
                </div>

                {/* Step 2: Pick score */}
                {selectedTeam && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} transition={{ duration: 0.3 }}>
                    <p className="text-sm font-medium mb-2">שלב 2: מה תהיה התוצאה?</p>
                    <p className="text-xs text-muted-foreground mb-4">
                      ניצחון בלבד: <span className="text-foreground font-semibold">{WINNER_POINTS} נק'</span>
                      {' · '}
                      תוצאה מדויקת: <span className="text-foreground font-semibold">עד 14 נק'</span>
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {filteredOdds.map(o => {
                        const isSelected = selectedOutcome === o.outcome;
                        const diffBg = getDifficultyOpacity(o.multiplier || 1);
                        const points = exactScorePoints(o.american_odds ?? null);
                        return (
                          <button
                            key={o.outcome}
                            onClick={() => setSelectedOutcome(o.outcome)}
                            disabled={isPastDeadline && !isGodMode}
                            className={`${diffBg} border rounded-xl p-4 text-center transition-all ${
                              isSelected
                                ? 'border-accent bg-accent/15 btn-accent-glow'
                                : 'border-border hover:border-muted-foreground'
                            } disabled:opacity-30 disabled:cursor-not-allowed`}
                          >
                            <p className="text-2xl font-bold">{o.team_wins}-{o.opponent_wins}</p>
                            <p className="text-accent font-semibold text-sm mt-1">
                              {points} נק'
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ) : (
              <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-10">
                <div className="w-16 h-16 mx-auto rounded-full bg-green-500/10 flex items-center justify-center mb-4">
                  <Check className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-xl font-bold mb-2">
                  {isGodMode ? `ההימור עבור ${targetDisplayName} נשמר!` : 'ההימור נשמר!'}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {selectedOutcome} · עד {exactScorePoints(chosenOdd?.american_odds ?? null)} נק'
                </p>
                <Link to={`/group/${groupId}`} className="inline-block mt-6 text-sm text-accent hover:underline">
                  חזרה לקבוצה
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Sticky bottom bar */}
        {selectedOutcome && !submitted && (!isPastDeadline || isGodMode) && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed bottom-0 left-0 right-0 bg-card/90 backdrop-blur-md border-t border-border p-4 z-50"
          >
            <div className="container mx-auto max-w-2xl flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{selectedTeam} {selectedOutcome}</p>
                <p className="text-accent text-xs font-bold">
                  עד {exactScorePoints(chosenOdd?.american_odds ?? null)} נק' (ניצחון: {WINNER_POINTS} נק')
                </p>
              </div>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-accent hover:bg-accent/90 text-accent-foreground btn-accent-glow shrink-0"
              >
                {submitting
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : isGodMode
                  ? 'אשר הימור (ניהול)'
                  : hasExistingBet
                  ? 'עדכן הימור'
                  : 'אשר הימור'}
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </Layout>
  );
};

export default BetPlacement;
